import { AxisType, CoordinateInputTier3 } from "@/gql/types";
import { GEN_AI_MOCK } from "@/views/overview/sources/ms-365-gen-ai/data/mock";
import {
  AiAgentContentItem,
} from "@/views/overview/sources/ms-365-gen-ai/data/model";
import {
  GenAiDrillScope,
  get_gen_ai_drill_scope,
} from "@/views/overview/sources/ms-365-gen-ai/data/drill-scope";
import { load_rows } from "@/views/overview/sources/ms-365-gen-ai/data/package-data";

/**
 * Mocks for the Microsoft 365 Gen AI Governance drill-down.
 *
 * These feed the REAL `EventsModal` / `FilesModal`, so the app's own table,
 * filters and row components are reused as-is.
 *
 * NOTE: `gql2` short-circuits mocks BEFORE the query mapper runs (see
 * `src/gql/lib.ts` `use_mock`), so `eventParser` / `fileParser` never execute.
 * The payloads below must therefore already be in the POST-PARSER row shape.
 */

/**
 * EventTableRow renders `entity.email` on the FIRST line and `entity.name` on
 * the second, so the display name goes in `email` and the department/role in
 * `name` to match the design.
 */
const entity = (primary: string, secondary: string, external = false) => ({
  id: primary,
  name: secondary,
  email: primary,
  type: external ? "Person" : "Employee",
  isExternal: external,
  isPersonalAddress: false,
  department: secondary,
});

/* `to_event` lived here to reshape the hand-written AiAgentEvent objects into
   post-parser rows. scripts/build-gen-ai-mock.js now does that projection ahead
   of time, straight from the package, so the rows arrive ready to render. */

const to_file = (c: AiAgentContentItem) => ({
  id: c.id,
  name: c.name,
  subType: "File",
  category: "Content",
  externalId: c.id,
  clusterUID: "",
  path: "/Microsoft 365/",
  hasHistory: false,
  showConfidence: false,
  type: "File",
  is_email_body: false,
  sensitivity: c.sensitivity,
  file_extension: c.name.split(".").pop() ?? "",
  labelType: "Content",
  label_code: "",
  parentId: c.info_type,
  source: c.application === "msft_onedrive" ? "onedrive" : "sharepoint",
  has_adhoc_label: false,
  adhoc_label_name: "",
  has_context_labels: 1,
  archive: null,
  archive_items_tooltip: [],
  piis: c.piis
    ? c.piis.split(", ").map((name, i) => ({
        id: `${c.id}-pii-${i}`,
        name,
        labelCategory: "PII",
      }))
    : [],
  hide_menu: true,
  ctxLabels: [
    {
      id: `${c.id}-ctx`,
      name: c.context,
      category: "Department",
      is_folder: false,
    },
  ],
  information: { id: c.info_type, name: c.info_type, type: "Content" },
  details: { url: `https://cognni.sharepoint.com/${c.name}` },
});

/**
 * The severity levels the query is asking for, read off the coordinates the
 * modal built. `gql2` hands the mock the same input the real call would get,
 * so the drill-down's pinned severity AND anything the user picks in the Risk
 * Severity filter both arrive here — `merge_and_fix_cords` has already spread
 * the filter's axis over the pinned one, so this is the effective selection.
 *
 * Returns null when severity is unconstrained, which means "do not filter"
 * rather than "match nothing".
 */
const requested_severities = (cords?: CoordinateInputTier3[]) =>
  axis_values(cords, AxisType.Severity).map(Number);

/**
 * Every value the modal's filters put on a given axis.
 *
 * The filter controls ride the same coordinates as the drill-down's own pins —
 * `merge_and_fix_cords` has already spread the user's selection over whatever
 * was pinned — so reading the axis here yields the effective choice. Reading
 * only Severity, as this did before, left Applications and Event type inert:
 * the list never changed no matter what was picked.
 */
const axis_values = (
  cords: CoordinateInputTier3[] | undefined,
  type: AxisType
): string[] => {
  if (!cords || !cords.length) {
    return [];
  }
  return cords.reduce((acc, cord) => {
    [cord.x, cord.y].forEach((axis) => {
      if (axis && axis.type === type && axis.values) {
        acc.push(...axis.values.map(String));
      }
    });
    return acc;
  }, [] as string[]);
};

/**
 * The real event data, from the `final-copilot-mock-data-v1` package.
 *
 * Fetched rather than imported: the package is 79MB of GraphQL payloads, and
 * bundling it would put all of that through webpack. `scripts/build-gen-ai-mock.js`
 * projects it into the post-parser row shape under `public/mock/gen-ai/`, which
 * vue-cli copies verbatim; only the file behind the clicked number is loaded.
 *
 * Each widget has its own population — the severity card's 261 interactions and
 * the matrix's 7,792 are different event sets, not one list sliced two ways —
 * so the file is chosen by the widget recorded in the drill scope.
 */
/** which file answers this drill-down */
const file_for = (scope: GenAiDrillScope | null) => {
  if (!scope || !scope.widget) {
    return "sample";
  }
  if (scope.widget === "matrix") {
    // split per cell, so opening one does not pull all 7,792 rows
    return scope.category && scope.relation
      ? `matrix-${scope.category}__${scope.relation}`
      : "sample";
  }
  return scope.widget;
};

export const gen_ai_gql = {
  "exposure-events": {
    delay: 400,
    payload: async ({
      cursor,
      cords,
    }: {
      cursor: string;
      cords?: CoordinateInputTier3[];
    }) => {
      // a single page — a non-empty cursor would make UseCollection loop forever
      if (cursor) {
        return { cursor: "", objects: [] };
      }
      const scope = get_gen_ai_drill_scope();
      const rows = await load_rows(file_for(scope));

      const levels = requested_severities(cords);
      const apps = axis_values(cords, AxisType.Application);
      const sens = (scope && scope.sensitivities) || null;

      const objects = rows.filter((e) => {
        // `severity: null` means the event carries no risk at all, which is
        // distinct from risk severity 0 ("None"); such rows belong only in an
        // unconstrained list, never in a severity drill-down
        if (levels.length && (e.severity === null || !levels.includes(e.severity))) {
          return false;
        }
        // the classification cards pin their slider here; sensitivity is a
        // different axis from severity and must not be conflated with it
        if (sens && sens.length && !sens.includes(e._sens)) {
          return false;
        }
        if (apps.length && !apps.includes(e._app)) {
          return false;
        }
        if (scope) {
          if (scope.relation && e._rel !== scope.relation) return false;
          if (scope.category && e._cat !== scope.category) return false;
          if (scope.actor && e._by !== scope.actor) return false;
        }
        return true;
      });
      return { cursor: "", objects };
    },
  },
  get_files: {
    delay: 400,
    payload: async ({ cursor }: { cursor: string }) =>
      cursor
        ? { cursor: "", objects: [] }
        : { cursor: "", objects: GEN_AI_MOCK.content.map(to_file) },
  },
};
